import { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle, FileText, Loader2, RefreshCw, Video } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const NOTE_PROVIDERS = ['microsoft', 'web'];

const PlayerNote = ({ media }) => (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="link-player-note">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {media?.provider === 'microsoft' ? (
            <span>
                {media.folder ? 'This is a folder link. ' : ''}Not showing? Sign in to Microsoft 365 (Outlook or Teams) in this browser with your work account, then press Reload.
                If it still does not load, ask your admin to check you have access to this file.
            </span>
        ) : (
            <span>Not showing? Some websites do not allow their content inside other apps. Press Reload, or ask your admin for a OneDrive, SharePoint or YouTube link.</span>
        )}
    </div>
);

const Frame = ({ media, title, tall, reloadKey, onVideoError }) => (media.mode === 'video' ? (
    <video key={reloadKey} src={media.src} controls autoPlay playsInline controlsList="nodownload" disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()} onError={onVideoError}
        className="aspect-video w-full rounded-lg bg-black" data-testid="link-player-video" />
) : (
    <iframe key={reloadKey} src={media.src} title={title}
        className={`w-full rounded-lg border bg-muted ${tall ? 'h-[68vh]' : 'aspect-video'}`}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen referrerPolicy="strict-origin-when-cross-origin"
        sandbox="allow-scripts allow-same-origin allow-forms allow-presentation"
        data-testid="link-player-iframe" />
));

// Resolves a course link (OneDrive/SharePoint, YouTube, Vimeo, Drive, direct file) and plays it inside the page
export const LinkPlayer = ({ url, title, kind = 'video', reloadKey = 0, onResolved }) => {
    const { api } = useAuth();
    const apiRef = useRef(api);
    apiRef.current = api;
    const onResolvedRef = useRef(onResolved);
    onResolvedRef.current = onResolved;
    const [media, setMedia] = useState(null);
    const [videoFailed, setVideoFailed] = useState(false);

    useEffect(() => {
        if (!url) return undefined;
        let alive = true;
        setMedia(null);
        setVideoFailed(false);
        apiRef.current.get('/media/resolve', { params: { url } })
            .then((res) => res.data)
            .catch(() => ({ mode: 'iframe', provider: 'web', src: url }))
            .then((data) => { if (alive) { setMedia(data); onResolvedRef.current?.(data); } });
        return () => { alive = false; };
    }, [url, reloadKey]);

    if (!media) {
        return (
            <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-muted" data-testid="link-player-loading">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }
    const broken = media.mode === 'unsupported' || videoFailed;
    return (
        <div className="space-y-3" data-testid="link-player">
            {broken ? (
                <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-muted p-6 text-center text-sm text-muted-foreground" data-testid="link-player-unavailable">
                    This content could not be played here. Press Reload to try again.
                </div>
            ) : (
                <Frame media={media} title={title} tall={media.folder || !(media.video ?? kind === 'video')} reloadKey={reloadKey} onVideoError={() => setVideoFailed(true)} />
            )}
            {(broken || (media.mode === 'iframe' && NOTE_PROVIDERS.includes(media.provider))) && <PlayerNote media={media} />}
        </div>
    );
};

// Same-page dialog for course links, replacing the old "open in new tab" behaviour
export const LinkPlayerDialog = ({ item, onClose, onComplete, completed }) => {
    const [reloadKey, setReloadKey] = useState(0);
    const [resolvedVideo, setResolvedVideo] = useState(null);
    useEffect(() => { setResolvedVideo(null); }, [item?.url]);
    const Icon = (resolvedVideo ?? item?.kind === 'video') ? Video : FileText;
    return (
        <Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-h-[94vh] max-w-5xl overflow-y-auto bg-background" data-testid="link-player-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 pr-6" data-testid="link-player-title">
                        <Icon className="h-5 w-5 shrink-0 text-blue-500" />{item?.title}
                    </DialogTitle>
                    <DialogDescription>Plays right here, so you stay on your training page.</DialogDescription>
                </DialogHeader>
                {item && <LinkPlayer url={item.url} title={item.title} kind={item.kind} reloadKey={reloadKey} onResolved={(m) => setResolvedVideo(m.video ?? null)} />}
                <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
                    <p className="text-xs text-muted-foreground">This content is protected and cannot be downloaded</p>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setReloadKey((k) => k + 1)} data-testid="link-player-reload">
                            <RefreshCw className="mr-2 h-4 w-4" />Reload
                        </Button>
                        <Button variant="outline" onClick={onClose} data-testid="link-player-close">Close</Button>
                        {onComplete && !completed && (
                            <Button onClick={onComplete} data-testid="link-player-complete">
                                <CheckCircle className="mr-2 h-4 w-4" />Mark Complete
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
